import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { KeycloakAuthGuard } from 'keycloak-angular';
import { Apollo, gql } from 'apollo-angular';
import { SELECTED_APPLICATION_QUERY } from '../pages/page-gql';
import { Utilities } from '@common/utils';
import { CssRoles, FscdIntakeSessionStorageKeys } from '@common/constants';
import { FSCD_APPLICATION_DETAIL } from '../pages/gql-fragments';
import { ContextProvider, ContextService } from '@common/ui/shared-components';
import { AuthenticationService } from '@govalta-emu/keycloak-auth-service';
import { KeycloakService } from 'keycloak-angular';
import { lastValueFrom } from 'rxjs';

const GQL_SECURE_APPLICATION = gql`
query application($id: String!) {
  application(id: $id) {
    ${FSCD_APPLICATION_DETAIL}
  }
}`;

@Injectable({
  providedIn: 'root',
})
export class IntakeApplicationAuthGuard extends KeycloakAuthGuard {
  utils = Utilities;
  defaultNotAuthorized = '/forbidden';
  defaultInvalid = '/invalid';
  defaultTimeout = '/timeout';

  constructor(
    protected readonly router: Router,
    protected readonly keycloakService: KeycloakService,
    protected readonly authenticationService: AuthenticationService,
    private apollo: Apollo,
    private contextProvider: ContextProvider,
    private contextService: ContextService
  ) {
    super(router, keycloakService);
  }

  public async isAccessAllowed(route: ActivatedRouteSnapshot): Promise<boolean> {
    this.apollo.client.writeQuery({
      query: SELECTED_APPLICATION_QUERY,
      data: {
        selectedApplicationId: route.params.applicationId,
        isSubmitted: false, //TODO: implement localstorage for not parameterized client graph fields
      },
    });

    let granted = false;

    //get application email
    const applicationId = route.params.applicationId;

    // make sure the application is cached
    const applicationUserId = await this.getApplicationAuthorizedUserId(applicationId); //authorized keycloak id for requesting application

    let userid = null;
    let isAdmin = false;
    if (this.authenticated) {
      const user = await this.contextService.getContextUserProfile(false);
      this.contextProvider.setCurrentUser(user);
      userid = user._id;
      isAdmin = user.roles?.some((role) => role == CssRoles.CSSAdmin.code);
    } else {
      this.contextProvider.setCurrentUser(null);
    }

    if (isAdmin) return (granted = true);

    if (applicationUserId !== -1 && applicationUserId !== -2) {
      //get current logged in user keycloak id

      if (userid !== null && userid === applicationUserId) {
        //If current user is also the authorizedUser.
        granted = true;
      } else if (applicationUserId === null) {
        //If the application has no authorizedUser then:

        // Check the session storage to see if the user's application session is valid,
        // and only grant them access to that application for that browser session.
        const sessionApplicationIds = this.utils.getSessionStorageArray<string>(
          FscdIntakeSessionStorageKeys.IntakeApplicationSessionId.key
        );
        if (sessionApplicationIds.indexOf(applicationId) >= 0) {
          granted = true;
        }
      }
    } else if (applicationUserId === -1) {
      //invalid application ID request
      const invalidPage = route?.data?.invalidPage ?? this.defaultInvalid;
      const timeoutPage = route?.data?.timeoutPage ?? this.defaultTimeout;
      if (this.authenticated) {
        this.router.navigate([invalidPage]);
      } else {
        //not authenticated, user is timed out
        this.router.navigate([timeoutPage]);
      }

      return false;
    }

    if (granted === false) {
      const notAuthorizedPage = route?.data?.notAuthorizedPage ?? this.defaultNotAuthorized;
      this.router.navigate([notAuthorizedPage]);
      return false;
    }

    return true;
  }

  //returns -1 for invalid applications
  //returns -2 for valid application but not authorized
  private async getApplicationAuthorizedUserId(applicationId: string) {
    const applicationQuery = await this.apollo.query({
      query: GQL_SECURE_APPLICATION,
      variables: {
        id: applicationId,
      },
    });

    if (!applicationQuery) {
      return -1;
    }

    let validApplication = false;
    const authorizedUserId = lastValueFrom(applicationQuery)
      .then((result) => {
        //this will get keycloak id that's authorized to access this application. valid application if it gets in here.

        //the application property will be null application object will be null if authorizedUser does not match current user.
        validApplication = true;

        let application = null;

        application = result.data['application'];

        const applicationUserId = application.authorizedUser;
        return applicationUserId;
      })
      .catch((ex) => {
        if (validApplication || ex?.message === 'Forbidden') {
          return -2;
        }
        return -1; //using -1 to indicate an error due to application does not exist with this application id
      });
    return authorizedUserId;
  }
}
